import { Link } from '@tanstack/react-router';
import { Settings } from 'lucide-react';
import { type ComponentProps, useState } from 'react';

import { NavUser } from '@/components/nav-user';
import { SettingsDialog } from '@/components/settings-dialog';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useAuth } from '@/lib/auth';
import { NAV_GROUPS, type NavItem } from '@/lib/navigation';

/**
 * The console's navigation, mirroring ctrl-saas's team-account sidebar.
 *
 * Built on shadcn's primitives rather than the generated `nav-*` blocks: those
 * render plain `<a href>`, which full-reloads a SPA on every click. TanStack
 * Router's `Link` keeps navigation client-side and marks the matched item.
 *
 * Settings sits outside the groups because it opens a dialog rather than
 * navigating — changing a tax rate mid-order should not lose the order.
 */

/**
 * Highlight for the section you are in.
 *
 * `SidebarMenuButton` styles its own `data-active`, but with `asChild` the
 * element that ends up in the DOM is the `Link`, and what the Link knows about
 * is `data-status` — set to `active` on a prefix match, so `/orders/abc` keeps
 * Orders lit. Styling that attribute takes the router's answer directly rather
 * than keeping a second copy of "which route am I on" in this component.
 */
const ACTIVE =
  'data-[status=active]:bg-sidebar-accent data-[status=active]:font-medium data-[status=active]:text-sidebar-accent-foreground';
export function AppSidebar(props: ComponentProps<typeof Sidebar>) {
  const { claims, activeAccountId } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const role = activeAccountId ? (claims.rolesByAccount[activeAccountId] ?? '') : '';

  /**
   * Hide sections the member cannot open.
   *
   * `resources` is the per-member capability list; the RLS policies added in
   * 20260828020000 return nothing for a section they lack, so showing the link
   * would only lead to a permanently empty screen.
   */
  const held = new Set(activeAccountId ? (claims.resourcesByAccount[activeAccountId] ?? []) : []);
  const isOwnerish = claims.isSuperAdmin || role === 'owner';

  const canOpen = (item: NavItem) => !item.resource || isOwnerish || held.has(item.resource);

  return (
    <>
      <Sidebar variant="inset" {...props}>
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild>
                <Link to="/">
                  <img src="/icon-192.png" alt="" className="size-8 rounded-lg" />
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">ctrl</span>
                    <span className="text-muted-foreground truncate text-xs capitalize">
                      {role || 'Operator console'}
                    </span>
                  </div>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          {NAV_GROUPS.map((group) => {
            const items = group.items.filter(canOpen);
            if (!items.length) return null;

            return (
              <SidebarGroup key={group.label}>
                <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
                <SidebarMenu>
                  {items.map((item) => (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton asChild tooltip={item.title} className={ACTIVE}>
                        <Link to={item.to} activeOptions={{ exact: item.to === '/' }}>
                          <item.icon />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroup>
            );
          })}

          <SidebarGroup className="mt-auto">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton tooltip="Settings" onClick={() => setSettingsOpen(true)}>
                  <Settings />
                  <span>Settings</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <NavUser />
        </SidebarFooter>
      </Sidebar>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
}
